import React from 'react';
import { WizardStep } from '@app/app/_components/Wizard/Wizard';
import { TravelersStep } from '@app/app/esales/travel/_sections/steps/TravelersStep/TravelersStep';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { InsuredTraveler } from '@app/domain/model/eshop/travel/InsuredTraveler';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTravelInsuranceResult } from '@app/service/eshop/travel/actions/purchaseTravelInsurance';
import { TRAVEL_INSURANCE_STEPS } from '@app/app/esales/travel/_sections/steps/TRAVEL_INSURANCE_STEPS';
import { InsuranceRisksStep } from '@app/app/esales/travel/_sections/steps/InsuranceRisksStep/InsuranceRisksStep';
import { TravelPremiumResponse } from '@app/domain/model/eshop/travel/TravelPremiumResponse';
import { TravelPremiumRisk } from '@app/domain/model/eshop/travel/TravelPremiumRisk';
import { CustomerLoginStep } from '@app/app/esales/_sections/steps/CustomerLoginStep';
import { Client } from '@app/domain/model/Client';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { PolicyholderInformationStep } from '@app/app/esales/travel/_sections/steps/PolicyholderInformationStep/PolicyholderInformationStep';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { InsuredPersonsStep } from '@app/app/esales/travel/_sections/steps/InsuredPersonsStep/InsuredPersonsStep';
import { SummaryStep } from '@app/app/esales/travel/_sections/steps/SummaryStep/SummaryStep';
import { TravelInsuranceRequestResult } from '@app/service/eshop/travel/actions/requestTravelInsurance';
import {
    PolicyholderFormFields,
    PolicyholderFormHandlers,
} from '@app/app/esales/travel/_sections/TravelWizardSection/hooks/usePolicyholderFormFields';
import { Card } from '@app/app/_components/Card/Card';
import { PaymentMethodTypeStep } from '@app/app/esales/travel/_sections/steps/PaymentMethodTypeStep';
import { PaymentMethodType } from '@app/domain/model/PaymentMethodType';
import { PaymentMethodStep } from '@app/app/esales/_sections/steps/PaymentMethodStep';
import { PlatformSettings } from '@app/domain/model/PlatformSettings';

interface Props {
    t: (key: string) => string;
    handleDiscountCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleApplyDiscountClick: () => Promise<void>;
    discountCodeData: DiscountCodeData;
    insuredPersons: InsuredTraveler[];
    handleAddPerson: () => void;
    handleRemovePerson: (id: string) => void;
    handleAgeChange: (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    formState: FormState<PurchaseTravelInsuranceResult>;
    handleRisksChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    currentPremiums: Nullable<TravelPremiumResponse>;
    priceState: {
        selectedRisks: TravelPremiumRisk[];
        totalPrice: number;
    };
    authState: AuthState;
    clients: Client[];
    swedbankLogoSrc: string;
    sebLogoSrc: string;
    onSuccessfulLogin: () => void;
    isCompany: boolean;
    handleNameChange: (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSurnameChange: (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handlePersonalCodeChange: (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    onChangeNumberOfPersons: () => void;
    onEditPolicyholderInfo: () => void;
    changeAgeOfTraveler: () => void;
    editTravelers: () => void;
    requestResult: TravelInsuranceRequestResult;
    policyholderFields: PolicyholderFormFields;
    policyholderHandlers: PolicyholderFormHandlers;
    onSavePolicyholder: (data: PolicyholderFormFields) => void;
    onSaveTravelers: (travelers: InsuredTraveler[]) => void;
    editTravelConditions: () => void;
    editPolicy: () => void;
    selectedPaymentMethodType: Nullable<string>;
    handlePaymentMethodTypeChange: (paymentMethodType: Nullable<string>) => void;
    settings: PlatformSettings;
    shouldSkipInsuredPersonsStep: boolean;
}

export function buildSteps({
    t,
    handleDiscountCode,
    handleApplyDiscountClick,
    discountCodeData,
    insuredPersons,
    handleAddPerson,
    handleRemovePerson,
    handleAgeChange,
    formState,
    currentPremiums,
    handleRisksChange,
    priceState,
    authState,
    clients,
    swedbankLogoSrc,
    sebLogoSrc,
    onSuccessfulLogin,
    isCompany,
    handleNameChange,
    handleSurnameChange,
    handlePersonalCodeChange,
    onChangeNumberOfPersons,
    onEditPolicyholderInfo,
    changeAgeOfTraveler,
    editTravelers,
    requestResult,
    policyholderFields,
    policyholderHandlers,
    onSavePolicyholder,
    onSaveTravelers,
    editTravelConditions,
    editPolicy,
    selectedPaymentMethodType,
    handlePaymentMethodTypeChange,
    settings,
    shouldSkipInsuredPersonsStep,
}: Props): WizardStep[] {
    return [
        {
            label: t('eshop.travel.travel'),
            subSteps: [
                {
                    key: TRAVEL_INSURANCE_STEPS.TRAVELERS_SETUP,
                    title: t('eshop.travel.travelers'),
                    body: (
                        <TravelersStep
                            handleDiscountCode={handleDiscountCode}
                            handleApplyDiscountClick={handleApplyDiscountClick}
                            discountCodeData={discountCodeData}
                            insuredPersons={insuredPersons}
                            handleAddPerson={handleAddPerson}
                            handleRemovePerson={handleRemovePerson}
                            handleAgeChange={handleAgeChange}
                            violations={formState.violations}
                            personalCode={formState.data?.formData?.personalCode}
                        />
                    ),
                },
                {
                    key: TRAVEL_INSURANCE_STEPS.CHOOSE_INSURANCE,
                    title: t('eshop.travel.chooseInsurance'),
                    body: (
                        <InsuranceRisksStep
                            risks={currentPremiums?.risks || []}
                            onChange={handleRisksChange}
                            value={priceState.selectedRisks.map(risk => risk.code || '')}
                        />
                    ),
                },
                {
                    key: TRAVEL_INSURANCE_STEPS.CUSTOMER_LOGIN,
                    when: () =>
                        settings.general.esalesTravelAuthEnabled &&
                        (!authState.authenticated || !authState.clientSelected),
                    title: t('eshop.general.ifYouAlreadyClient'),
                    body: (
                        <CustomerLoginStep
                            swedbankLogoSrc={swedbankLogoSrc}
                            sebLogoSrc={sebLogoSrc}
                            clients={clients}
                            authState={authState}
                            onContinue={onSuccessfulLogin}
                            onSuccess={onSuccessfulLogin}
                            bankAuthEnabled={settings.general.esalesTravelBankAuthEnabled}
                        />
                    ),
                },
                {
                    key: TRAVEL_INSURANCE_STEPS.POLICYHOLDER_INFORMATION,
                    title: (
                        <div className="flex w-full flex-row items-center justify-between">
                            <span>{t('eshop.travel.policyHolder')}</span>
                            <InformationPopover
                                content={<Paragraph>{t('eshop.travel.policyHolderInfo')}</Paragraph>}
                                className="absolute right-0">
                                <QuestionCircleIcon className="size-6 text-neutral-500" />
                            </InformationPopover>
                        </div>
                    ),
                    body: (
                        <Card className="flex flex-col gap-8 p-6">
                            <PolicyholderInformationStep
                                formState={formState}
                                isCompany={isCompany}
                                policyholderFields={policyholderFields}
                                policyholderHandlers={policyholderHandlers}
                                personalCode={formState.data?.formData?.personalCode || ''}
                            />
                        </Card>
                    ),
                },
                {
                    key: TRAVEL_INSURANCE_STEPS.INSURED_PERSONS,
                    when: () => !shouldSkipInsuredPersonsStep,
                    title: formState.data?.formData?.policyholderIsTraveler
                        ? t('eshop.travel.insuredPeople')
                        : t('eshop.travel.policyHolderAndInsured'),
                    body: (
                        <InsuredPersonsStep
                            insuredPersons={insuredPersons}
                            handleNameChange={handleNameChange}
                            handleSurnameChange={handleSurnameChange}
                            handlePersonalCodeChange={handlePersonalCodeChange}
                            violations={formState.violations}
                            policyholderIsTraveler={formState.data?.formData?.policyholderIsTraveler || false}
                            onChangeNumberOfPersons={onChangeNumberOfPersons}
                            formState={formState}
                            editPolicyholderInfo={onEditPolicyholderInfo}
                            changeAgeOfTraveler={changeAgeOfTraveler}
                            isCompany={isCompany}
                        />
                    ),
                },
                {
                    key: TRAVEL_INSURANCE_STEPS.SUMMARY,
                    title: t('eshop.travel.summary'),
                    body: (
                        <SummaryStep
                            requestResult={requestResult}
                            formState={formState}
                            policyholderFields={policyholderFields}
                            isCompany={isCompany}
                            personalCode={formState.data?.formData?.personalCode || ''}
                            onSavePolicyholder={onSavePolicyholder}
                            policyholderIsTraveler={formState.data?.formData?.policyholderIsTraveler || false}
                            insuredPersons={insuredPersons}
                            onChangeNumberOfPersons={onChangeNumberOfPersons}
                            editPolicyholderInfo={onEditPolicyholderInfo}
                            changeAgeOfTraveler={changeAgeOfTraveler}
                            editTravelers={editTravelers}
                            onSaveTravelers={onSaveTravelers}
                            editTravelConditions={editTravelConditions}
                            editPolicy={editPolicy}
                        />
                    ),
                },
            ],
        },
        {
            label: t('eshop.travel.paymentStepTitle'),
            subSteps: [
                {
                    key: TRAVEL_INSURANCE_STEPS.PAYMENT.METHOD_TYPE,
                    title: t('eshop.general.choosePaymentMethod'),
                    body: <PaymentMethodTypeStep formState={formState} onChange={handlePaymentMethodTypeChange} />,
                },
                {
                    key: TRAVEL_INSURANCE_STEPS.PAYMENT.BANKS,
                    when: () => selectedPaymentMethodType === PaymentMethodType.Banks,
                    title: t('eshop.general.choosePaymentMethod'),
                    body: <PaymentMethodStep paymentMethods={formState.data?.paymentMethods ?? []} />,
                },
            ],
        },
    ];
}
